import { useFormik } from "formik";
import { FundingForm } from "../types";
import InputField from "./InputField";
import CountryDropdown from "components/shared/SelectCountry/CountryDropdown";
import CurrencyDropdown from "components/shared/SelectCurrency/CurrencyDropdown";
import TextArea from "./TextArea";
import StateDropdown from "components/shared/SelectState/StateDropdown";
import UploadFile from "./UploadFile";
import { step2Schema } from "./formSchema";
import { convertToNumber } from "utils";

interface FundingDetailsProps {
  onNext: (values: Partial<FundingForm>) => void;
  onBack: () => void;
  initialData: FundingForm;
  onFieldChange: (field: keyof FundingForm, value: any) => void;
}

const FundingDetails = ({
  onNext,
  onBack,
  initialData,
  onFieldChange,
}: FundingDetailsProps) => {
  const formik = useFormik({
    initialValues: {
      uploadedFiles: initialData.uploadedFiles,
      projectDescription: initialData.projectDescription,
      country: initialData.country,
      state: initialData.state,
      currency: initialData.currency,
      amount: initialData.amount,
    },
    validationSchema: step2Schema,
    onSubmit: (values) => onNext(values),
  });

  const handleNumberChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;
      if (val === "") {
        formik.setFieldValue(field, "");
        return;
      }
      if (/[^0-9.,]/.test(val)) return;

      if (val.endsWith(".")) {
        formik.setFieldValue(field, val);
        return;
      }

      let num = convertToNumber(val);
      if (!isNaN(num)) {
        let formatedValue = new Intl.NumberFormat("en-US").format(num);
        formik.setFieldValue(field, formatedValue);
      }
    };

  const handleChange = (e: React.ChangeEvent<any>) => {
    formik.handleChange(e);
    onFieldChange(e.target.name as keyof FundingForm, e.target.value);
  };

  return (
    <form onSubmit={formik.handleSubmit} className='flex flex-col'>
      <h2 className='text-2xl text-blueShades-4 font-bold pb-2'>
        Funding Details
      </h2>
      <p className='text-sm text-ashShade-16'>
        Give us information of your funding
      </p>
      <div className='flex flex-col gap-6 pt-6'>
        <UploadFile
          values={formik.values as FundingForm}
          setFieldValue={formik.setFieldValue}
          onFieldChange={onFieldChange}
        />
        <TextArea
          name='projectDescription'
          value={formik.values.projectDescription}
          error={
            formik.touched.projectDescription
              ? formik.errors.projectDescription
              : ""
          }
          onChange={handleChange}
          onBlur={formik.handleBlur}
          labelText='Description of Project Requiring Funding'
          placeholder='Enter a description...'
          maxLength={160}
          isRequired
        />
        <div className='flex flex-col gap-3'>
          <h3 className='text-sm text-blueShades-4 font-SatoshiMedium'>
            Project Location
          </h3>
          <div className='flex flex-col md:flex-row items-start w-full gap-6'>
            <div className='w-full'>
              <CountryDropdown
                onSelect={(country) => {
                  formik.setFieldValue("country", country.name, false);
                  onFieldChange("country", country.name);
                }}
                value={formik.values.country}
                labelText='Country'
                isRequired
                error={formik.touched.country ? formik.errors.country : ""}
              />
            </div>
            <div className='w-full'>
              <StateDropdown
                country={formik.values.country}
                onSelect={(state) => {
                  formik.setFieldValue("state", state, false);
                  onFieldChange("state", state);
                }}
                value={formik.values.state}
                labelText='State/Province'
                isRequired
                error={formik.touched.state ? formik.errors.state : ""}
              />
            </div>
          </div>
        </div>
        <div className='flex flex-col gap-3'>
          <h3 className='text-sm text-blueShades-4 font-SatoshiMedium'>
            Funding Amount
          </h3>
          <div className='flex flex-col md:flex-row items-start gap-6'>
            <div className='w-full'>
              <CurrencyDropdown
                onSelect={(currency) => {
                  formik.setFieldValue("currency", currency.code, false);
                  onFieldChange("currency", currency.code);
                }}
                value={formik.values.currency}
                labelText='Currency'
                isRequired
                error={formik.touched.currency ? formik.errors.currency : ""}
              />
            </div>
            <InputField
              type='text'
              name='amount'
              labelText='Amount'
              placeholder='e.g 200,000,000'
              onChange={(e) => {
                handleNumberChange("amount")(e);
                onFieldChange("amount", e.target.value);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.amount}
              error={formik.touched.amount ? formik.errors.amount : ""}
              isRequired
            />
          </div>
        </div>
      </div>
      <div className='flex gap-6 items-center pt-6 self-end'>
        <button
          onClick={onBack}
          type='button'
          className='bg-transparent border border-ashShade-19 px-[18px] py-[9px] text-sm text-blueShades-6 rounded-lg'
        >
          Back
        </button>
        <button
          type='submit'
          className='bg-blueShades-6 border border-blueShades-6 px-[18px] py-[9px] text-sm text-white rounded-lg'
        >
          Next
        </button>
      </div>
    </form>
  );
};
export default FundingDetails;
